<script setup>
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import MainLayout from "@/components/layout/MainLayout.vue";
import UnitProfileCard from "@/components/units/UnitProfileCard.vue";
import UnitResidentCard from "@/components/units/UnitResidentCard.vue";
import UnitFinancialSummaryCard from "@/components/units/UnitFinancialSummaryCard.vue";
import UnitInvoiceHistory from "@/components/units/UnitInvoiceHistory.vue";
import UnitPaymentHistory from "@/components/units/UnitPaymentHistory.vue";
import UnitFormModal from "@/components/units/UnitFormModal.vue";
import { useUnitStore } from "@/stores/unit";
import { usePrint } from "@/composables/usePrint";

const route = useRoute();
const router = useRouter();
const unitStore = useUnitStore();
const activeTab = ref("invoices");

const unitFormModal = ref(null);

const unitId = computed(() => route.params.id);
const unit = computed(() => unitStore.getUnitById(unitId.value));
const invoiceItems = computed(() =>
  unitStore.getInvoicesByUnitId(unitId.value),
);
const paymentItems = computed(() =>
  unitStore.getPaymentsByUnitId(unitId.value),
);

const goBack = () => router.push("/units");
const unitTypeText = (type) =>
  ({ house: "บ้าน", condo: "ห้องชุด", shop: "ร้านค้า" }[type] || type);

const handleEdit = () => unitFormModal.value?.open(unit.value);

const { printUnit } = usePrint();
const handlePrint = () =>
  printUnit(unit.value, invoiceItems.value, paymentItems.value);
</script>

<template>
  <MainLayout>
    <div class="container-fluid px-0">
      <div v-if="unit" class="vstack gap-4">
        <div
          class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
        >
          <div>
            <button
              type="button"
              class="btn btn-secondary border rounded-3 mb-3"
              @click="goBack"
            >
              <i class="bi bi-arrow-left me-1"></i>กลับไปรายการยูนิต
            </button>
            <div class="d-flex align-items-center gap-3 flex-wrap">
              <div class="unit-icon"><i class="bi bi-house-door"></i></div>
              <div>
                <h3 class="fw-bold mb-1">
                  {{ unit.unitCode }} - {{ unit.houseNo }}
                </h3>
                <div class="text-muted">
                  {{ unit.building }} • {{ unit.zone }} •
                  {{ unitTypeText(unit.unitType) }}
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex gap-2 flex-wrap">
            <button
              type="button"
              class="btn btn-secondary rounded-3"
              @click="handlePrint"
            >
              <i class="bi bi-printer me-1"></i>พิมพ์ข้อมูลยูนิต
            </button>
            <button
              type="button"
              class="btn btn-primary rounded-3"
              @click="handleEdit"
            >
              <i class="bi bi-pencil-square me-1"></i>แก้ไขยูนิต
            </button>
          </div>
        </div>

        <div class="row g-4">
          <div class="col-xl-4"><UnitResidentCard :unit="unit" /></div>
          <div class="col-xl-4"><UnitFinancialSummaryCard :unit="unit" /></div>
          <div class="col-xl-4"><UnitProfileCard :unit="unit" /></div>
        </div>

        <div class="card border-0 shadow-sm rounded-4 overflow-hidden">
          <div class="card-header bg-white border-0 p-3">
            <ul class="nav nav-pills gap-2">
              <li class="nav-item">
                <button
                  type="button"
                  class="nav-link rounded-pill px-4"
                  :class="
                    activeTab === 'invoices' ? 'active' : 'text-dark bg-light'
                  "
                  @click="activeTab = 'invoices'"
                >
                  <i class="bi bi-receipt me-2"></i>ประวัติใบแจ้งหนี้
                </button>
              </li>
              <li class="nav-item">
                <button
                  type="button"
                  class="nav-link rounded-pill px-4"
                  :class="
                    activeTab === 'payments' ? 'active' : 'text-dark bg-light'
                  "
                  @click="activeTab = 'payments'"
                >
                  <i class="bi bi-cash-coin me-2"></i>ประวัติการชำระเงิน
                </button>
              </li>
            </ul>
          </div>
          <div class="card-body p-4 bg-light-subtle">
            <UnitInvoiceHistory
              v-if="activeTab === 'invoices'"
              :items="invoiceItems"
            />
            <UnitPaymentHistory v-else :items="paymentItems" />
          </div>
        </div>
      </div>

      <div v-else class="card border-0 shadow-sm rounded-4">
        <div class="card-body p-5 text-center">
          <i class="bi bi-search fs-1 text-secondary d-block mb-3"></i>
          <h4 class="fw-bold mb-2">ไม่พบข้อมูลยูนิต</h4>
          <p class="text-muted mb-4">
            อาจเป็นเพราะลิงก์ไม่ถูกต้อง หรือยังไม่มีข้อมูลยูนิตนี้ในระบบ
          </p>
          <button
            type="button"
            class="btn btn-primary rounded-3 px-4"
            @click="goBack"
          >
            <i class="bi bi-arrow-left me-2"></i>กลับไปหน้ารายการยูนิต
          </button>
        </div>
      </div>
    </div>

    <UnitFormModal ref="unitFormModal" @saved="" />
  </MainLayout>
</template>

<style scoped>
.unit-icon {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: rgba(13, 110, 253, 0.12);
  color: #0d6efd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}
</style>
